import { useState, useEffect, useRef, useCallback } from 'react';
import { useInterval } from './useInterval';
import { supabase } from '../supabaseClient';

const hasUsableLatestData = (data) =>
  Boolean(
    data &&
    data.time_utc &&
    (
      data.wind_speed_kph != null ||
      data.wind_gust_kph != null ||
      data.pm2_5_ugm3 != null ||
      data.pm10_ugm3 != null
    )
  );

const fetchLatestData = async (site) => {
  let latestData = JSON.parse(localStorage.getItem(`${site}_lastLatestData`) || 'null');

  try {
    const [
      { data: latestMet, error: metError },
      { data: latestPm, error: pmError },
    ] = await Promise.all([
      supabase
        .from('ivanhoe_weather_met')
        .select('time_utc, wind_speed_kph, wind_gust_kph, wind_direction')
        .eq('site', site)
        .order('time_utc', { ascending: false })
        .limit(1)
        .maybeSingle(),
      supabase
        .from('ivanhoe_weather_pm')
        .select('time_utc, pm2_5_ugm3, pm10_ugm3')
        .eq('site', site)
        .order('time_utc', { ascending: false })
        .limit(1)
        .maybeSingle(),
    ]);

    if (metError) {
      throw metError;
    }

    if (pmError) {
      throw pmError;
    }

    const fetched = {
      time_utc: latestMet?.time_utc || latestPm?.time_utc || null,
      wind_speed_kph: latestMet?.wind_speed_kph ?? null,
      wind_gust_kph: latestMet?.wind_gust_kph ?? null,
      wind_direction: latestMet?.wind_direction ?? null,
      pm2_5_ugm3: latestPm?.pm2_5_ugm3 ?? null,
      pm10_ugm3: latestPm?.pm10_ugm3 ?? null,
    };

    if (hasUsableLatestData(fetched)) {
      latestData = fetched;
      localStorage.setItem(`${site}_lastLatestData`, JSON.stringify(latestData));
    } else {
      console.warn(`No latest weather data returned for ${site}. Using cached data if available.`);
    }
  } catch (err) {
    console.error(`Error fetching latest data for ${site}: ${err.message}`);
  }

  return hasUsableLatestData(latestData) ? latestData : null;
};

export const useLatestData = (site, refreshInterval = 5, enabled = true) => {
  const [latestData, setLatestData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const requestIdRef = useRef(0);

  const fetchData = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    if (!site) {
      setError('Site is required');
      setLoading(false);
      return;
    }

    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    setLoading(true);
    setError(null);

    try {
      const data = await fetchLatestData(site);
      if (requestId !== requestIdRef.current) {
        return;
      }
      setLatestData(data);
    } catch (err) {
      if (requestId !== requestIdRef.current) {
        return;
      }

      setError(`Failed to fetch latest data: ${err.message}`);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [site, enabled]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    fetchData();
  }, [fetchData, refreshInterval, enabled]);

  useInterval(() => {
    if (enabled && refreshInterval > 0) {
      fetchData();
    }
  }, enabled && refreshInterval > 0 ? refreshInterval * 60 * 1000 : null);

  return { latestData, loading, error, fetchData };
};
